import React, { FC } from 'react'

import { useQuery } from '@apollo/client'
import { Controller, useFormContext } from 'react-hook-form'

import { GET_CURRENT_ACCOUNT, GET_CURRENT_LOCATION } from 'Const'
import { SignUpStates } from 'Hooks/checkout'
import useToggle from 'Hooks/useToggle'
import { Inquiry, useInquiry } from 'Utils/inquiry'

import PasswordField from 'Components/CustomerFieldsGroup/PasswordField'
import ErrorMessage from 'Components/ErrorMessage'
import FormErrors from 'Components/FormErrors'

import { Checkbox } from '../Checkbox'
import { Field } from '../Field'

interface Props {
  state: Inquiry<SignUpStates>
}

export const Details: FC<Props> = ({ state }) => {
  const { data: currentLocation } = useQuery(GET_CURRENT_LOCATION)
  const { data: currentAccount } = useQuery(GET_CURRENT_ACCOUNT)
  const { control, errors, register } = useFormContext()
  const [wantPassword, toggleWantPassword] = useToggle()
  const loginState = useInquiry(currentAccount?.customerAccountsState ?? '')
  const isPassword = state.isPassword

  return (
    <div hidden={!state.isForm && !state.isMultiple}>
      <div className="user-profile-login__name-row">
        <div className="user-profile-login__name-col">
          <Field htmlFor="profile-login-first-name" label="First name">
            <input
              ref={register({ required: 'First name is required' })}
              required
              aria-describedby={errors.firstName ? 'profile-login-first-name-error' : undefined}
              aria-invalid={Boolean(errors.firstName)}
              autoComplete="cc-given-name"
              id="profile-login-first-name"
              name="firstName"
              placeholder="Enter your first name..."
              readOnly={isPassword}
              type="text"
            />
            <ErrorMessage as={FormErrors} id="profile-login-first-name-error" name="firstName" />
          </Field>
        </div>

        <div className="user-profile-login__name-col">
          <Field htmlFor="profile-login-last-name" label="Last name">
            <input
              ref={register({ required: 'Last name is required' })}
              required
              aria-describedby={errors.lastName ? 'profile-login-last-name-error' : undefined}
              aria-invalid={Boolean(errors.lastName)}
              autoComplete="cc-family-name"
              id="profile-login-last-name"
              name="lastName"
              placeholder="Enter your last name..."
              readOnly={isPassword}
              type="text"
            />
            <ErrorMessage as={FormErrors} id="profile-login-last-name-error" name="lastName" />
          </Field>
        </div>
      </div>

      <Field htmlFor="profile-login-email" label="Email">
        <input
          ref={register({
            required: 'Email is required',
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: 'Email incorrect',
            },
          })}
          aria-describedby={errors.email ? 'profile-login-email-error' : undefined}
          aria-invalid={Boolean(errors.email)}
          autoComplete="email"
          id="profile-login-email"
          name="email"
          placeholder="Enter your email..."
          readOnly={isPassword}
          required={false}
          type="email"
        />
        <ErrorMessage as={FormErrors} id="profile-login-email-error" name="email" />
      </Field>

      {isPassword ? null : (
        <>
          <Controller
            defaultValue
            control={control}
            name="subscribed"
            render={(field) => (
              <Checkbox
                checked={Boolean(field.value)}
                id="profile-login-subscribed"
                label="Keep me up to date on news and exclusive offers via email"
                onChange={field.onChange}
              />
            )}
          />

          {currentLocation?.canSubscribeViaSms ? (
            <Controller
              control={control}
              defaultValue={false}
              name="subscribedSms"
              render={(field) => (
                <Checkbox
                  checked={Boolean(field.value)}
                  id="profile-login-subscribed-sms"
                  label="Keep me up to date on news and exclusive offers via text"
                  onChange={field.onChange}
                />
              )}
            />
          ) : null}

          {loginState.isDisabled ? null : (
            <>
              {loginState.isOptional ? (
                <Checkbox
                  checked={wantPassword}
                  id="profile-login-want-password"
                  label="Enter a password for faster checkout"
                  onChange={toggleWantPassword}
                />
              ) : null}

              <div className="user-profile-login__field-group" hidden={!(loginState.isRequired || wantPassword)}>
                <PasswordField
                  autoComplete="new-password"
                  label={
                    loginState.isRequired
                      ? 'Accounts are required to checkout. Please create a password'
                      : 'Please create a password'
                  }
                  required={loginState.isRequired || wantPassword}
                  wrapperClassName="user-profile-field"
                />
              </div>
            </>
          )}
        </>
      )}
    </div>
  )
}
